/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/

import { useContext } from 'react';
import { UseQueryOptions, keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { MainContext, UrlString } from 'openvsx-webui';
import {
  FetchedSeries,
  getRegistryAnalyticsAgents,
  getRegistryAnalyticsExtensions,
  getRegistryAnalyticsNamespaces,
  getRegistryAnalyticsOverview,
  getRegistryAnalyticsSeries,
  getRegistryAnalyticsStatus,
  getRegistryAnalyticsVersionSeries,
  getRegistryAnalyticsVersions
} from './analytics-service';
import { RegistryAnalyticsInterval } from './analytics-types';
import { splitExtensionRef } from './analytics-utils';

// TanStack queries over the registry analytics endpoints. All of them require analytics
// access, so callers gate with `options.enabled` on the status query below.

/**
 * Poll cadence of the data queries. Data stays fresh for the same window (staleTime), so
 * remounts and refocus within it serve the cache — the poll is the only refresh.
 */
export const ANALYTICS_POLL_INTERVAL = 120_000;

/**
 * The section's access gate: no payload advertises analytics access, the endpoint answering at
 * all is the capability. 403 (no access) and 404 (module not deployed) both mean "hide", so the
 * query never retries and callers must treat anything but `data` as no access.
 */
export const useAnalyticsStatus = (options?: { enabled?: boolean }) => {
  const { service, userLoading } = useContext(MainContext);
  return useQuery({
    queryKey: ['registry-analytics', 'status'],
    staleTime: ANALYTICS_POLL_INTERVAL,
    retry: false,
    queryFn: ({ signal }) => getRegistryAnalyticsStatus(service.serverUrl, signal),
    ...options,
    // the endpoint answers 403 until the session is established; wait out the auth bootstrap
    enabled: (options?.enabled ?? true) && !userLoading
  });
};

export const useAnalyticsOverview = (
  params: {
    from: string;
    to: string;
    interval: RegistryAnalyticsInterval;
    agents: string[];
    namespaces?: string[];
    category?: string;
  },
  options?: { enabled?: boolean }
) => {
  const { service } = useContext(MainContext);
  return useQuery({
    queryKey: ['registry-analytics', 'overview', params],
    refetchInterval: ANALYTICS_POLL_INTERVAL,
    staleTime: ANALYTICS_POLL_INTERVAL,
    queryFn: ({ signal }) => getRegistryAnalyticsOverview(service.serverUrl, signal, params),
    ...options
  });
};

/** Offset-paged leaderboard, following the search page's infinite scroll conventions. */
export const useInfiniteAnalyticsExtensions = (
  params: {
    query?: string;
    category?: string;
    namespaces?: string[];
    agents: string[];
    from: string;
    to: string;
    interval: RegistryAnalyticsInterval;
    sort: 'downloads' | 'growth' | 'name';
    direction: 'asc' | 'desc';
    size: number;
  },
  options?: { enabled?: boolean }
) => {
  const { service } = useContext(MainContext);
  return useInfiniteQuery({
    queryKey: ['registry-analytics', 'extensions', 'infinite', params],
    refetchInterval: ANALYTICS_POLL_INTERVAL,
    staleTime: ANALYTICS_POLL_INTERVAL,
    queryFn: ({ pageParam, signal }) =>
      getRegistryAnalyticsExtensions(service.serverUrl, signal, { ...params, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.extensions.length, 0);
      return lastPage.extensions.length > 0 && loaded < lastPage.totalSize ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
    ...options
  });
};

/**
 * Namespace options for the leaderboard's namespace filter, matched server-side. Doesn't poll —
 * it's an options list, fetched per input and kept warm while typing (placeholder data).
 */
export const useAnalyticsNamespaces = (
  params: { query?: string; category?: string; agents: string[]; from: string; to: string },
  options?: { enabled?: boolean }
) => {
  const { service } = useContext(MainContext);
  return useQuery({
    queryKey: ['registry-analytics', 'namespaces', params],
    staleTime: ANALYTICS_POLL_INTERVAL,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => getRegistryAnalyticsNamespaces(service.serverUrl, signal, params),
    ...options
  });
};

export const useAnalyticsSeries = (
  params: {
    /** 'namespace.name' references, optionally pinned to one version with an '@version' suffix. */
    extensions: string[];
    agents: string[];
    from: string;
    to: string;
    interval: RegistryAnalyticsInterval;
  },
  options?: { enabled?: boolean; placeholderData?: UseQueryOptions<Readonly<FetchedSeries>>['placeholderData'] }
) => {
  const { service } = useContext(MainContext);
  return useQuery({
    queryKey: ['registry-analytics', 'series', params],
    refetchInterval: ANALYTICS_POLL_INTERVAL,
    staleTime: ANALYTICS_POLL_INTERVAL,
    queryFn: ({ signal }) => getRegistryAnalyticsSeries(service.serverUrl, signal, params),
    ...options
  });
};

/**
 * Per-version series of one extension, for the stacked adoption chart. `version` is the pinned
 * version, which the API then always keeps apart instead of folding it into the remainder.
 */
export const useAnalyticsVersionSeries = (
  params: {
    extension: string;
    agents: string[];
    from: string;
    to: string;
    interval: RegistryAnalyticsInterval;
    version?: string;
  },
  options?: { enabled?: boolean }
) => {
  const { service } = useContext(MainContext);
  return useQuery({
    queryKey: ['registry-analytics', 'version-series', params],
    refetchInterval: ANALYTICS_POLL_INTERVAL,
    staleTime: ANALYTICS_POLL_INTERVAL,
    queryFn: ({ signal }) => getRegistryAnalyticsVersionSeries(service.serverUrl, signal, params),
    ...options
  });
};

export const useAnalyticsVersions = (
  params: { extension: string; agents: string[]; from: string; to: string },
  options?: { enabled?: boolean }
) => {
  const { service } = useContext(MainContext);
  return useQuery({
    queryKey: ['registry-analytics', 'versions', params],
    refetchInterval: ANALYTICS_POLL_INTERVAL,
    staleTime: ANALYTICS_POLL_INTERVAL,
    queryFn: ({ signal }) => getRegistryAnalyticsVersions(service.serverUrl, signal, params),
    ...options
  });
};

/** Public extension metadata of one 'namespace.name' reference, for the focused breakdown. */
export const useExtensionMetadata = (ref: string, options?: { enabled?: boolean }) => {
  const { service } = useContext(MainContext);
  const [namespace, name] = splitExtensionRef(ref);
  return useQuery({
    queryKey: ['registry-analytics', 'extension-metadata', ref],
    staleTime: ANALYTICS_POLL_INTERVAL,
    queryFn: async ({ signal }) => {
      return service.getExtensionDetail(signal, service.getExtensionApiUrl({ namespace, name }));
    },
    ...options,
    // a reference without a namespace (a hand-edited URL) names no extension to fetch
    enabled: (options?.enabled ?? true) && namespace.length > 0
  });
};

/**
 * Client ranking behind the status response's capability URL; disabled while the caller lacks it.
 * Doesn't poll: it's an options list, and a registry-wide aggregation every interval.
 */
export const useAnalyticsAgents = (
  agentsUrl: UrlString | undefined,
  params: { from: string; to: string },
  options?: { enabled?: boolean }
) => {
  // no serverUrl needed: the capability URL from the status response is the whole endpoint
  return useQuery({
    // an input of the query function, so it belongs in the key
    queryKey: ['registry-analytics', 'agents', agentsUrl, params],
    staleTime: ANALYTICS_POLL_INTERVAL,
    queryFn: ({ signal }) => {
      if (agentsUrl === undefined) {
        return Promise.reject(new Error('The caller lacks the agents capability.'));
      }
      return getRegistryAnalyticsAgents(signal, agentsUrl, params);
    },
    ...options,
    enabled: (options?.enabled ?? true) && agentsUrl !== undefined
  });
};
